import { normalizeDiscordId } from '../utils/discord.js';
import { normalizePlayer } from '../utils/players.js';

const getConfig = () => {
  if (!window.SUPABASE_CONFIG) {
    throw new Error('Supabase config missing. Please provide supabase.config.js.');
  }
  return window.SUPABASE_CONFIG;
};

const baseHeaders = () => {
  const { anonKey } = getConfig();
  return {
    apikey: anonKey,
    Authorization: `Bearer ${anonKey}`
  };
};

const request = async (path, { method = 'GET', headers = {}, body, signal } = {}) => {
  const { url } = getConfig();
  const res = await fetch(`${url}/rest/v1/${path}`, {
    method,
    headers: {
      ...baseHeaders(),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    },
    body: body ? JSON.stringify(body) : undefined,
    signal
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      message = data?.message || data?.error || message;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message = text;
    }
    throw new Error(message);
  }
  if (res.status === 204) return null;
  return res.json();
};

const normalizeCharacter = (row) => ({
  id: row.id,
  slug: row.slug || slugify(row.character_name || row.name || ''),
  name: row.character_name || row.name || '',
  player: row.player || '',
  discord_id: normalizeDiscordId(row.discord_id),
  status: row.status || 'Unknown',
  race: row.race || '',
  class: row.class || '',
  level: row.level ?? '',
  current_xp: row.current_xp ?? '',
  current_location: row.current_location || row.location || '',
  time_jail: row.time_jail || '',
  alignment: row.alignment || '',
  deity: row.deity || '',
  languages: row.languages || '',
  secondary_skills: row.secondary_skills || '',
  non_weapon_proficiencies: row.non_weapon_proficiencies || '',
  special_interest: row.special_interest || '',
  birthday: row.birthday || '',
  age: row.age || '',
  birth_location: row.birth_location || '',
  first_adventure: row.first_adventure || '',
  deathday: row.deathday || '',
  career_length: row.career_length || '',
  cause_of_death: row.cause_of_death || '',
  portrait_url: row.portrait_url || '',
  str: row.strength || row.str || '',
  int: row.intelligence || row.int || '',
  wis: row.wisdom || row.wis || '',
  dex: row.dexterity || row.dex || '',
  con: row.constitution || row.con || '',
  cha: row.charisma || row.cha || '',
  com: row.comeliness || row.com || '',
  raw: row
});

const normalizeLocation = (row) => ({
  id: row.id,
  name: row.name || row.location_name || '',
  hexNumber: row.hex_number || row.hex || '',
  diseaseModifier:
    Number(row.disease_modifier ?? row.disease_modifer ?? row.disease_modifier_percent ?? 0) || 0,
  parasiteModifier:
    Number(row.parasite_modifier ?? row.parasite_modifer ?? row.parasite_modifier_percent ?? 0) || 0,
  notes: row.notes || '',
  raw: row
});

const normalizePlayerRow = (row) => normalizePlayer(row);

const normalizeSession = (row) => ({
  id: row.id,
  session_number: row.session_number ?? '',
  session_date_played: row.session_date_played || '',
  in_game_session_ended: row.in_game_session_ended || '',
  characters_present: row.characters_present || '',
  notes: row.notes || '',
  created_at: row.created_at || ''
});

const normalizeNeverForgetEvent = (row) => ({
  id: row.id,
  event_name: row.event_name || '',
  event_date: row.event_date || '',
  description: row.description || '',
  created_at: row.created_at || ''
});

const normalizeLevelUpEvent = (row) => ({
  id: row.id,
  character_name: row.character_name || '',
  from_level: row.from_level || '',
  to_level: row.to_level || '',
  event_date: row.event_date || '',
  created_at: row.created_at || ''
});

const CHARACTER_FIELD_ALIASES = {
  character_name: ['character_name', 'name'],
  player: ['player'],
  discord_id: ['discord_id'],
  status: ['status'],
  race: ['race'],
  class: ['class'],
  level: ['level'],
  current_xp: ['current_xp'],
  current_location: ['current_location'],
  time_jail: ['time_jail', 'Time Jail'],
  alignment: ['alignment'],
  deity: ['deity'],
  languages: ['languages'],
  secondary_skills: ['secondary_skills'],
  non_weapon_proficiencies: ['non_weapon_proficiencies'],
  special_interest: ['special_interest'],
  birthday: ['birthday'],
  age: ['age'],
  birth_location: ['birth_location'],
  first_adventure: ['first_adventure'],
  deathday: ['deathday'],
  career_length: ['career_length'],
  cause_of_death: ['cause_of_death'],
  portrait_url: ['portrait_url'],
  strength: ['str', 'strength'],
  dexterity: ['dex', 'dexterity'],
  constitution: ['con', 'constitution'],
  intelligence: ['int', 'intelligence'],
  wisdom: ['wis', 'wisdom'],
  charisma: ['cha', 'charisma'],
  comeliness: ['com', 'comeliness']
};

const hasOwn = (source, key) => Object.prototype.hasOwnProperty.call(source || {}, key);

const getFirstCreateValue = (source, aliases) => {
  for (const alias of aliases) {
    const value = source?.[alias];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const getFirstPatchValue = (source, aliases) => {
  for (const alias of aliases) {
    if (hasOwn(source, alias)) return source[alias];
  }
  return undefined;
};

const normalizeCharacterMutationValue = (column, value) => {
  if (column === 'discord_id') return normalizeDiscordId(value);
  return value ?? '';
};

const buildCharacterCreatePayload = (data) => {
  const payload = {};

  for (const [column, aliases] of Object.entries(CHARACTER_FIELD_ALIASES)) {
    payload[column] = normalizeCharacterMutationValue(column, getFirstCreateValue(data, aliases));
  }

  return payload;
};

const buildCharacterPatchPayload = (updates) => {
  const payload = {};

  for (const [column, aliases] of Object.entries(CHARACTER_FIELD_ALIASES)) {
    const value = getFirstPatchValue(updates, aliases);
    if (value === undefined) continue;
    payload[column] = normalizeCharacterMutationValue(column, value);
  }

  return payload;
};

const slugify = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const encodeFilterValue = (value) => encodeURIComponent(String(value ?? ''));

export const uploadPortrait = async (file) => {
  const { url } = getConfig();
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const res = await fetch(`${url}/storage/v1/object/portraits/${path}`, {
    method: 'POST',
    headers: {
      ...baseHeaders(),
      'Content-Type': file.type || 'application/octet-stream'
    },
    body: file
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      message = data?.message || data?.error || message;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message = text;
    }
    throw new Error(message);
  }
  return `${url}/storage/v1/object/public/portraits/${path}`;
};

export const fetchCharacters = async () => {
  const data = await request('characters?select=*');
  return data.map(normalizeCharacter);
};

export const fetchLocations = async () => {
  const data = await request('locations?select=*&order=location_name.asc').catch(() =>
    request('locations?select=*')
  );
  return data.map(normalizeLocation).filter((location) => location.name);
};

export const fetchPlayers = async () => {
  const data = await request('players?select=*&order=name.asc').catch(() =>
    request('players?select=*')
  );
  return data.map(normalizePlayerRow).filter((player) => player.name);
};

export const fetchSessions = async () => {
  const data = await request('sessions?select=*&order=session_date_played.desc').catch(() =>
    request('sessions?select=*')
  );
  return data.map(normalizeSession);
};

export const createCharacter = async (payload) => {
  const [row] = await request('characters', {
    method: 'POST',
    body: buildCharacterCreatePayload(payload),
    headers: { Prefer: 'return=representation' }
  });
  return normalizeCharacter(row);
};

export const createLocation = async (payload) => {
  const diseaseModifier = payload?.disease_modifier;
  const parasiteModifier = payload?.parasite_modifier;
  const [row] = await request('locations', {
    method: 'POST',
    body: {
      location_name: String(payload?.location_name || '').trim(),
      hex_number: String(payload?.hex_number || '').trim() || null,
      disease_modifier:
        diseaseModifier === undefined || diseaseModifier === '' ? null : Number(diseaseModifier),
      parasite_modifier:
        parasiteModifier === undefined || parasiteModifier === '' ? null : Number(parasiteModifier),
      notes: payload?.notes || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeLocation(row);
};

export const updateLocation = async (id, payload) => {
  const diseaseModifier = payload?.disease_modifier;
  const parasiteModifier = payload?.parasite_modifier;
  const [row] = await request(`locations?id=eq.${encodeFilterValue(id)}`, {
    method: 'PATCH',
    body: {
      location_name: String(payload?.location_name || '').trim(),
      hex_number: String(payload?.hex_number || '').trim() || null,
      disease_modifier:
        diseaseModifier === undefined || diseaseModifier === '' ? null : Number(diseaseModifier),
      parasite_modifier:
        parasiteModifier === undefined || parasiteModifier === '' ? null : Number(parasiteModifier),
      notes: payload?.notes || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeLocation(row);
};

export const renameLocationOnCharacters = async (oldName, newName) => {
  const trimmedOld = String(oldName || '').trim();
  const trimmedNew = String(newName || '').trim();
  if (!trimmedOld || trimmedOld.toLowerCase() === trimmedNew.toLowerCase()) return;
  await Promise.all([
    request(`characters?current_location=ilike.${encodeFilterValue(trimmedOld)}`, {
      method: 'PATCH',
      body: { current_location: trimmedNew },
      headers: { Prefer: 'return=minimal' }
    }),
    request(`characters?birth_location=ilike.${encodeFilterValue(trimmedOld)}`, {
      method: 'PATCH',
      body: { birth_location: trimmedNew },
      headers: { Prefer: 'return=minimal' }
    })
  ]);
};

export const createPlayer = async (payload) => {
  const [row] = await request('players', {
    method: 'POST',
    body: {
      name: String(payload?.name || '').trim(),
      discord_id: normalizeDiscordId(payload?.discord_id) || null
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizePlayerRow(row);
};

export const updatePlayer = async (id, payload) => {
  const [row] = await request(`players?id=eq.${encodeFilterValue(id)}`, {
    method: 'PATCH',
    body: {
      name: String(payload?.name || '').trim(),
      discord_id: normalizeDiscordId(payload?.discord_id) || null
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizePlayerRow(row);
};

export const updateCharactersPlayerName = async (oldName, newName, discordId) => {
  const trimmedOld = String(oldName || '').trim();
  if (!trimmedOld) return;
  await request(`characters?player=ilike.${encodeFilterValue(trimmedOld)}`, {
    method: 'PATCH',
    body: buildCharacterPatchPayload({ player: newName, discord_id: discordId }),
    headers: { Prefer: 'return=minimal' }
  });
};

export const createSession = async (payload) => {
  const sessionNumber = payload?.session_number ? Number(payload.session_number) : null;
  const [row] = await request('sessions', {
    method: 'POST',
    body: {
      session_number: Number.isFinite(sessionNumber) ? sessionNumber : null,
      session_date_played: payload?.session_date_played || null,
      in_game_session_ended: payload?.in_game_session_ended || null,
      characters_present: payload?.characters_present || '',
      notes: payload?.notes || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeSession(row);
};

export const updateSession = async (id, payload) => {
  const sessionNumber = payload?.session_number ? Number(payload.session_number) : null;
  const [row] = await request(`sessions?id=eq.${encodeFilterValue(id)}`, {
    method: 'PATCH',
    body: {
      session_number: Number.isFinite(sessionNumber) ? sessionNumber : null,
      session_date_played: payload?.session_date_played || null,
      in_game_session_ended: payload?.in_game_session_ended || null,
      characters_present: payload?.characters_present || '',
      notes: payload?.notes || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeSession(row);
};

export const deleteSession = async (id) => {
  await request(`sessions?id=eq.${encodeFilterValue(id)}`, {
    method: 'DELETE',
    headers: { Prefer: 'return=minimal' }
  });
};

export const updateNeverForgetEvent = async (id, payload) => {
  const [row] = await request(`never_forget_events?id=eq.${encodeFilterValue(id)}`, {
    method: 'PATCH',
    body: {
      event_name: String(payload?.event_name || '').trim(),
      event_date: payload?.event_date || null,
      description: payload?.description || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeNeverForgetEvent(row);
};

export const fetchNeverForgetEvents = async () => {
  const data = await request('never_forget_events?select=*&order=event_date.desc').catch(() =>
    request('never_forget_events?select=*')
  );
  return data.map(normalizeNeverForgetEvent);
};

export const fetchLevelUpEvents = async () => {
  const data = await request('level_up_events?select=*&order=event_date.desc').catch(() =>
    request('level_up_events?select=*')
  );
  return data.map(normalizeLevelUpEvent);
};

export const createLevelUpEvent = async (payload) => {
  const [row] = await request('level_up_events', {
    method: 'POST',
    body: {
      character_name: String(payload?.character_name || '').trim(),
      from_level: payload?.from_level || '',
      to_level: String(payload?.to_level || '').trim()
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeLevelUpEvent(row);
};

export const createNeverForgetEvent = async (payload) => {
  const [row] = await request('never_forget_events', {
    method: 'POST',
    body: {
      event_name: String(payload?.event_name || '').trim(),
      event_date: payload?.event_date || null,
      description: payload?.description || ''
    },
    headers: { Prefer: 'return=representation' }
  });
  return normalizeNeverForgetEvent(row);
};

export const updateCharacter = async (id, updates) => {
  await request(`characters?id=eq.${id}`, {
    method: 'PATCH',
    body: buildCharacterPatchPayload(updates),
    headers: { Prefer: 'return=minimal' }
  });
};

export const deleteCharacter = async (id) => {
  await request(`characters?id=eq.${encodeFilterValue(id)}`, {
    method: 'DELETE',
    headers: { Prefer: 'return=minimal' }
  });
};

